import { View, Text } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { format, isPast } from "date-fns";
import { nl } from "date-fns/locale";

/**
 * Hint interface for component
 */
interface HintData {
  id: string;
  text: string;
  imageUrl?: string;
  releaseAt: string;
  isReleased: boolean;
}

/**
 * Props for RevealHints
 */
interface RevealHintsProps {
  hints: HintData[];
  onRefresh?: () => void;
}

/**
 * Single hint card
 */
const HintCard = ({
  hint,
  index,
  colors,
}: {
  hint: HintData;
  index: number;
  colors: ReturnType<typeof useThemeColors>;
}) => {
  const isUnlocked = hint.isReleased || isPast(new Date(hint.releaseAt));
  const canUnlock = isPast(new Date(hint.releaseAt)) && !hint.isReleased;

  return (
    <View
      className={`rounded-xl p-4 mb-3 ${isUnlocked ? "" : "opacity-70"}`}
      style={{
        backgroundColor: isUnlocked ? colors.primaryLight : colors.surfaceSecondary,
        borderWidth: isUnlocked ? 0 : 1,
        borderColor: colors.border,
        borderStyle: isUnlocked ? "solid" : "dashed",
      }}
    >
      <View className="flex-row items-center">
        {/* Hint number badge */}
        <View
          className="w-8 h-8 rounded-full items-center justify-center mr-3"
          style={{
            backgroundColor: isUnlocked ? colors.primary : colors.textTertiary,
          }}
        >
          {isUnlocked ? (
            <Ionicons name="bulb" size={16} color="#FFFFFF" />
          ) : (
            <Text className="text-sm font-bold text-white">{index + 1}</Text>
          )}
        </View>

        <View className="flex-1">
          <Text
            className="font-medium"
            style={{ color: isUnlocked ? colors.text : colors.textTertiary }}
          >
            {isUnlocked ? `Hint ${index + 1}` : `Hint ${index + 1} - Vergrendeld`}
          </Text>

          {isUnlocked ? (
            <Text className="mt-1" style={{ color: colors.textSecondary }}>
              {hint.text}
            </Text>
          ) : (
            <Text className="text-sm mt-1" style={{ color: colors.textTertiary }}>
              Beschikbaar {format(new Date(hint.releaseAt), "d MMM HH:mm", { locale: nl })}
            </Text>
          )}
        </View>

        {/* Status icon */}
        <View className="ml-2">
          {isUnlocked ? (
            <Ionicons name="checkmark-circle" size={24} color={colors.success} />
          ) : canUnlock ? (
            <Ionicons name="lock-open" size={20} color={colors.primary} />
          ) : (
            <Ionicons name="lock-closed" size={20} color={colors.textTertiary} />
          )}
        </View>
      </View>
    </View>
  );
};

/**
 * RevealHints Component
 * Displays progressive hints for a reveal
 */
export const RevealHints = ({ hints, onRefresh }: RevealHintsProps) => {
  const colors = useThemeColors();

  // Hints are already sorted from parent
  const sortedHints = hints;

  // Count unlocked hints
  const unlockedCount = sortedHints.filter(
    (h) => h.isReleased || isPast(new Date(h.releaseAt))
  ).length;

  return (
    <View className="px-4">
      {/* Header */}
      <View className="flex-row items-center justify-between mb-4">
        <View className="flex-row items-center">
          <Ionicons name="bulb" size={24} color={colors.primary} />
          <Text className="ml-2 text-lg font-bold" style={{ color: colors.text }}>
            Hints
          </Text>
        </View>
        <View
          className="px-3 py-1 rounded-full"
          style={{ backgroundColor: colors.primaryLight }}
        >
          <Text className="text-sm font-medium" style={{ color: colors.primary }}>
            {unlockedCount} / {sortedHints.length}
          </Text>
        </View>
      </View>

      {/* Progress bar */}
      <View
        className="h-2 rounded-full mb-4 overflow-hidden"
        style={{ backgroundColor: colors.surfaceSecondary }}
      >
        <View
          className="h-full rounded-full"
          style={{
            backgroundColor: colors.primary,
            width: `${(unlockedCount / sortedHints.length) * 100}%`,
          }}
        />
      </View>

      {/* Hints list */}
      {sortedHints.map((hint, index) => (
        <HintCard key={hint.id} hint={hint} index={index} colors={colors} />
      ))}

      {/* All hints unlocked message */}
      {unlockedCount === sortedHints.length && (
        <View
          className="rounded-xl p-4 items-center"
          style={{ backgroundColor: colors.successLight }}
        >
          <Ionicons name="checkmark-circle" size={32} color={colors.success} />
          <Text className="mt-2 font-medium" style={{ color: colors.success }}>
            Alle hints ontgrendeld!
          </Text>
        </View>
      )}
    </View>
  );
};

export default RevealHints;

